import { useCallback, useEffect, useMemo, useState } from "react";
import {useForm} from "react-hook-form";
import {CHECKBOX_TYPE, CHOICE_TYPE, TEXT_TYPE} from "v4/data/fieldTypes";
import {CORE_TEMPLATE_LIST} from "v4/data/apiRoutes";
import useTemplatePdf from "v4/features/front/generatePdf/hooks/useTemplatePdf";
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import {generateUrl} from "v4/services/api.service";
import useAutoFetch from "v4/hooks/useAutoFetch";

/**
 * @param {string} quoteId
 * @param {string|null} [preselectTemplateId] - template à pré-sélectionner (« Re-générer »)
 * @param {function} updateHandler
 */
export default function useGeneratePdf({quoteId, preselectTemplateId, updateHandler}) {
    const {t} = usePolTranslation();
    const methods = useForm({mode: 'onChange'});
    const [isFilenameDisabled, setIsFilenameDisabled] = useState(true);

    const {
        data: templates,
        isLoading
    } = useAutoFetch(generateUrl(CORE_TEMPLATE_LIST, {id: quoteId, isNewVersion: true}))

    const {
        generate,
        isLoadingGenerate,
        pdfFile,
    } = useTemplatePdf(updateHandler);

    const onTemplateChange = useCallback((value) => {
        if (!templates?.data) return;

        const selectedTemplate = templates.data.find(({id}) => id === value);

        if (!selectedTemplate?.filename) {
            methods.setValue('filename', '');
            setIsFilenameDisabled(true);
            return;
        }

        methods.setValue('filename', selectedTemplate.filename.replace(/\.pdf$/i, ''), {
            shouldDirty: true,
            shouldValidate: true
        });
        setIsFilenameDisabled(!selectedTemplate.isNameModifiable);

        // Trigger focusin/focusout to make useFieldWrapperLabel detect the new value and move the label up
        requestAnimationFrame(() => { // Ensure this runs after the value has been updated in the DOM
            const filenameInput = document.querySelector('[id*="_filename"]');
            if (filenameInput) {
                filenameInput.dispatchEvent(new Event('focusin', {bubbles: true}));
                filenameInput.dispatchEvent(new Event('focusout', {bubbles: true}));
            }
        });
    }, [templates, methods]);

    const presetTemplateId = useMemo(() => {
        if (!templates?.data || !preselectTemplateId) return null;
        return templates.data.some(({id}) => id === preselectTemplateId) ? preselectTemplateId : null;
    }, [templates, preselectTemplateId]);

    useEffect(() => {
        if (presetTemplateId) onTemplateChange(presetTemplateId);
    }, [presetTemplateId, onTemplateChange]);

    const form = useMemo(() => {
        if (!templates?.data) return null;

        return {
            type: "success:get-form-schema",
            data: {
                template: {
                    type: CHOICE_TYPE,
                    label: t('template'),
                    constraints: {NotBlank: {}},
                    choices: templates.data.map(({id, label}) => ({value: id, label})),
                    specificParams: {menuPosition: 'fixed'},
                    onFieldChange: onTemplateChange,
                    value: presetTemplateId ?? undefined,
                },
                showPrices: {
                    type: CHECKBOX_TYPE,
                    label: t('hide_prices_in_quote_lines'),
                    value: false,
                },
                filename: {
                    type: TEXT_TYPE,
                    label: t('filename'),
                    constraints: {NotBlank: {}},
                    addon: '.pdf',
                    value: '',
                }
            }
        };
    }, [templates, t, onTemplateChange, presetTemplateId]);

    const handleSubmit = (data) => {
        generate({
            quoteId,
            template: data.template,
            showPrices: data.showPrices,
            filename: `${data.filename}.pdf`,
        });
    };

    return {
        form,
        methods,
        isLoading,
        isLoadingGenerate,
        isFilenameDisabled,
        pdfFile,
        handleSubmit,
    };
}
